import "bootstrap/dist/css/bootstrap.min.css";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { fetchOrders, updateOrder } from "../store/ordersSlice.js";
import { ref, update } from "firebase/database";
import { db } from "../firebase.js";
import { sendOrderEmail } from "../utils/emailService.js";

const AdminOrderManagement = () => {
  const dispatch = useDispatch();
  const orders = useSelector((s) => s.orders.items || []);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(""); // 'confirm', 'place', 'deliver', 'cancel'
  const [loading, setLoading] = useState(false);
  
  const [orderDetails, setOrderDetails] = useState({
    trackingId: "",
    trackingLink: "",
    estimatedDelivery: "",
    courierPartner: "",
    courierContact: "",
    cancellationReason: "",
    refundAmount: "",
    refundMethod: "",
  });

  useEffect(() => {
    dispatch(fetchOrders());
  }, [dispatch]);

  const openModal = (order, type) => {
    setSelectedOrder(order);
    setModalType(type);
    setShowModal(true);
    setOrderDetails({
      trackingId: order.trackingId || "",
      trackingLink: order.trackingLink || "",
      estimatedDelivery: order.estimatedDelivery || "",
      courierPartner: order.courierPartner || "",
      courierContact: order.courierContact || "",
      cancellationReason: "",
      refundAmount: order.total || "",
      refundMethod: "original",
    });
  };

  const sendEmail = async (order, type) => {
    const result = await sendOrderEmail(order, type);
    if (result.success) {
      toast.success(`📧 ${type === "shipped" ? "Shipping" : "Delivery"} email sent to ${order.email}`);
    } else {
      toast.warn(`Order updated but email failed: ${result.error}`);
    }
  };

  const handleUpdateOrderStatus = async () => {
    if (!selectedOrder) return;

    setLoading(true);
    try {
      let updatedData = {
        ...selectedOrder,
        updatedAt: Date.now(),
      };

      switch (modalType) {
        case "confirm":
          updatedData.status = "confirmed";
          updatedData.confirmedAt = Date.now();
          // No email on confirm
          break;

        case "place":
          if (!orderDetails.trackingId || !orderDetails.trackingLink) {
            toast.error("Please provide tracking details");
            setLoading(false);
            return;
          }
          updatedData.status = "placed";
          updatedData.placedAt = Date.now();
          updatedData.trackingId = orderDetails.trackingId;
          updatedData.trackingLink = orderDetails.trackingLink;
          updatedData.estimatedDelivery = orderDetails.estimatedDelivery;
          updatedData.courierPartner = orderDetails.courierPartner;
          updatedData.courierContact = orderDetails.courierContact;
          break;

        case "deliver":
          updatedData.status = "delivered";
          updatedData.deliveredAt = Date.now();
          updatedData.showRatingPopup = true;
          break;

        case "cancel":
          if (!orderDetails.cancellationReason) {
            toast.error("Please provide cancellation reason");
            setLoading(false);
            return;
          }
          updatedData.status = "cancellation_in_process";
          updatedData.cancellationReason = orderDetails.cancellationReason;
          updatedData.cancellationInitiatedAt = Date.now();
          break;

        case "refund":
          updatedData.status = "cancelled";
          updatedData.refundAmount = orderDetails.refundAmount;
          updatedData.refundMethod = orderDetails.refundMethod;
          updatedData.refundCompletedAt = Date.now();
          break;

        default:
          break;
      }

      // Update in Firebase — only write changed fields, never overwrite userId
      if (db) {
        const orderRef = ref(db, `orders/${selectedOrder.id}`);
        const { id, ...dataWithoutId } = updatedData;
        await update(orderRef, dataWithoutId);
      }

      // Update in Redux
      await dispatch(updateOrder({ id: selectedOrder.id, data: updatedData })).unwrap();

      toast.success("Order updated successfully!");
      setShowModal(false);
      setSelectedOrder(null);

      // Send email only for shipped (placed) and delivered
      if (modalType === "place") {
        await sendEmail(updatedData, "shipped");
      } else if (modalType === "deliver") {
        await sendEmail(updatedData, "delivered");
      }
    } catch (error) {
      console.error("Error updating order:", error);
      toast.error("Failed to update order");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: "warning", icon: "clock", text: "Pending" },
      confirmed: { bg: "info", icon: "check-circle", text: "Confirmed" },
      placed: { bg: "primary", icon: "truck", text: "Order Placed" },
      delivered: { bg: "success", icon: "check-circle-fill", text: "Delivered" },
      cancellation_in_process: { bg: "danger", icon: "x-circle", text: "Cancellation in Process" },
      cancelled: { bg: "secondary", icon: "x-circle-fill", text: "Cancelled" },
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`badge bg-${config.bg}`}>
        <i className={`bi bi-${config.icon} me-1`}></i>
        {config.text}
      </span>
    );
  };

  const getAvailableActions = (order) => {
    const actions = [];
    
    if (order.status === "pending") {
      actions.push({ type: "confirm", label: "Confirm Order", icon: "check-circle", color: "info" });
    }
    
    if (order.status === "confirmed") {
      actions.push({ type: "place", label: "Mark as Placed", icon: "truck", color: "primary" });
      actions.push({ type: "cancel", label: "Cancel Order", icon: "x-circle", color: "danger" });
    }
    
    if (order.status === "placed") {
      actions.push({ type: "deliver", label: "Mark as Delivered", icon: "check-circle-fill", color: "success" });
      actions.push({ type: "cancel", label: "Cancel Order", icon: "x-circle", color: "danger" });
    }
    
    if (order.status === "cancellation_in_process") {
      actions.push({ type: "refund", label: "Complete Refund", icon: "currency-rupee", color: "warning" });
    }

    return actions;
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fw-bold">
          <i className="bi bi-box-seam me-2"></i>Order Management
        </h2>
        <div className="d-flex gap-2">
          <span className="badge bg-warning px-3 py-2">
            Pending: {orders.filter(o => o.status === "pending").length}
          </span>
          <span className="badge bg-info px-3 py-2">
            Confirmed: {orders.filter(o => o.status === "confirmed").length}
          </span>
          <span className="badge bg-primary px-3 py-2">
            Placed: {orders.filter(o => o.status === "placed").length}
          </span>
          <span className="badge bg-success px-3 py-2">
            Delivered: {orders.filter(o => o.status === "delivered").length}
          </span>
        </div>
      </div>

      <div className="card border-0 shadow-sm rounded-4">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th>Order ID</th>
                  <th>Customer</th>
                  <th>Items</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Tracking</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-5">
                      <i className="bi bi-inbox display-4 d-block mb-3 text-muted"></i>
                      <h5 className="text-muted">No orders found</h5>
                    </td>
                  </tr>
                ) : (
                  orders.map((order) => (
                    <tr key={order.id}>
                      <td>
                        <div className="fw-bold text-primary">#{order.id?.slice(0, 8)}</div>
                        <small className="text-muted">{order.id?.slice(8, 16)}</small>
                      </td>
                      <td>
                        <div className="fw-medium">{order.name}</div>
                        <small className="text-muted">{order.email}</small>
                      </td>
                      <td>
                        <span className="badge bg-light text-dark">
                          {order.items?.length || 0} items
                        </span>
                      </td>
                      <td className="fw-bold text-success">₹{order.total?.toFixed(2)}</td>
                      <td>{getStatusBadge(order.status || "pending")}</td>
                      <td>
                        <div className="small">
                          {order.createdAt ? new Date(order.createdAt).toLocaleDateString() : "—"}
                        </div>
                        <small className="text-muted">
                          {order.createdAt ? new Date(order.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : ""}
                        </small>
                      </td>
                      <td>
                        {order.trackingLink ? (
                          <a
                            href={order.trackingLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline-primary"
                          >
                            <i className="bi bi-box-arrow-up-right me-1"></i>
                            Track
                          </a>
                        ) : (
                          <span className="text-muted small">—</span>
                        )}
                      </td>
                      <td>
                        <div className="d-flex gap-1 flex-wrap">
                          {getAvailableActions(order).map((action) => (
                            <button
                              key={action.type}
                              className={`btn btn-sm btn-outline-${action.color}`}
                              onClick={() => openModal(order, action.type)}
                              title={action.label}
                            >
                              <i className={`bi bi-${action.icon}`}></i>
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && selectedOrder && (
        <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {modalType === "confirm" && "Confirm Order"}
                  {modalType === "place" && "Mark Order as Placed"}
                  {modalType === "deliver" && "Mark Order as Delivered"}
                  {modalType === "cancel" && "Cancel Order"}
                  {modalType === "refund" && "Complete Refund"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <h6 className="fw-bold">Order Details</h6>
                  <div className="row">
                    <div className="col-md-6">
                      <p className="mb-1"><strong>Order ID:</strong> {selectedOrder.id?.slice(0, 16)}</p>
                      <p className="mb-1"><strong>Customer:</strong> {selectedOrder.name}</p>
                      <p className="mb-1"><strong>Email:</strong> {selectedOrder.email}</p>
                    </div>
                    <div className="col-md-6">
                      <p className="mb-1"><strong>Phone:</strong> {selectedOrder.phone}</p>
                      <p className="mb-1"><strong>Amount:</strong> ₹{selectedOrder.total?.toFixed(2)}</p>
                      <p className="mb-1"><strong>Items:</strong> {selectedOrder.items?.length || 0}</p>
                    </div>
                  </div>
                </div>

                <hr />

                {modalType === "confirm" && (
                  <div className="alert alert-info">
                    <i className="bi bi-info-circle me-2"></i>
                    Confirming this order will notify the customer that their order has been received and is being processed.
                  </div>
                )}

                {modalType === "place" && (
                  <div>
                    <h6 className="fw-bold mb-3">Shipping Details</h6>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label">Tracking ID *</label>
                        <input
                          type="text"
                          className="form-control"
                          value={orderDetails.trackingId}
                          onChange={(e) => setOrderDetails({ ...orderDetails, trackingId: e.target.value })}
                          placeholder="e.g., TRK123456789"
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Tracking Link *</label>
                        <input
                          type="url"
                          className="form-control"
                          value={orderDetails.trackingLink}
                          onChange={(e) => setOrderDetails({ ...orderDetails, trackingLink: e.target.value })}
                          placeholder="https://tracking.example.com/..."
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Courier Partner</label>
                        <select
                          className="form-select"
                          value={orderDetails.courierPartner}
                          onChange={(e) => setOrderDetails({ ...orderDetails, courierPartner: e.target.value })}
                        >
                          <option value="">Select Courier</option>
                          <option value="Blue Dart">Blue Dart</option>
                          <option value="DTDC">DTDC</option>
                          <option value="FedEx">FedEx</option>
                          <option value="Delhivery">Delhivery</option>
                          <option value="India Post">India Post</option>
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Courier Contact</label>
                        <input
                          type="tel"
                          className="form-control"
                          value={orderDetails.courierContact}
                          onChange={(e) => setOrderDetails({ ...orderDetails, courierContact: e.target.value })}
                          placeholder="Courier contact number"
                        />
                      </div>
                      <div className="col-md-12">
                        <label className="form-label">Estimated Delivery Date</label>
                        <input
                          type="date"
                          className="form-control"
                          value={orderDetails.estimatedDelivery}
                          onChange={(e) => setOrderDetails({ ...orderDetails, estimatedDelivery: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {modalType === "deliver" && (
                  <div className="alert alert-success">
                    <i className="bi bi-check-circle me-2"></i>
                    Marking this order as delivered will notify the customer and prompt them to rate their experience.
                  </div>
                )}

                {modalType === "cancel" && (
                  <div>
                    <div className="alert alert-warning">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      This will initiate the cancellation process. Customer will be notified.
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Cancellation Reason *</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={orderDetails.cancellationReason}
                        onChange={(e) => setOrderDetails({ ...orderDetails, cancellationReason: e.target.value })}
                        placeholder="Enter reason for cancellation..."
                      ></textarea>
                    </div>
                  </div>
                )}

                {modalType === "refund" && (
                  <div>
                    <div className="alert alert-info">
                      <i className="bi bi-info-circle me-2"></i>
                      Complete the refund process and update the customer.
                    </div>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label">Refund Amount</label>
                        <input
                          type="number"
                          className="form-control"
                          value={orderDetails.refundAmount}
                          onChange={(e) => setOrderDetails({ ...orderDetails, refundAmount: e.target.value })}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Refund Method</label>
                        <select
                          className="form-select"
                          value={orderDetails.refundMethod}
                          onChange={(e) => setOrderDetails({ ...orderDetails, refundMethod: e.target.value })}
                        >
                          <option value="original">Original Payment Method</option>
                          <option value="bank">Bank Transfer</option>
                          <option value="wallet">Wallet</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleUpdateOrderStatus}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Processing...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-circle me-2"></i>
                      Confirm
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrderManagement;
